import { FieldPath, UseFormReturn } from "react-hook-form";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Badge } from "@/components/ui/badge";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  requisitionStatusToBackgroundColor,
  requisitionStatusToHoverBackgroundColor,
  requisitionStatusToLabel,
  shiftTypeToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { Requisition } from "@/types";

const RequisitionSelectOption = ({
  requisition,
}: {
  requisition: Requisition;
}) => {
  return (
    <>
      <Column className="ml-2 items-start">
        <Row className="items-center gap-2">
          <span className="font-semibold">Req {requisition.id}</span>
          <Badge className="h-4" variant="secondary">
            {requisition.shiftType
              .map((shiftType) => shiftTypeToLabel[shiftType])
              .join(", ")}
          </Badge>
        </Row>
        <span className="text-xs text-slate-500">
          Requested:{" "}
          {requisition.dateRequested
            ? dates.formatIsoDateForUser(requisition.dateRequested)
            : "-"}
        </span>
      </Column>
      <Badge
        className={cn(
          "mr-4 h-6",
          requisitionStatusToBackgroundColor[requisition.status],
          requisitionStatusToHoverBackgroundColor[requisition.status],
        )}
        variant="secondary"
      >
        {requisitionStatusToLabel[requisition.status]}
      </Badge>
    </>
  );
};

export const ChangeStatusDialogRequisitionSelect = <
  T extends Record<string, unknown>,
>({
  disabled,
  fieldName,
  form,
  onRequisitionChange,
  requisitions,
}: {
  disabled?: boolean;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  onRequisitionChange?: (requisition?: Requisition) => void;
  requisitions: Requisition[];
}) => {
  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        return (
          <FormItem className="text-slate-700">
            <FormLabel>Requisition</FormLabel>
            <Select
              disabled={disabled}
              onValueChange={(value) => {
                const requisition = requisitions.find(
                  (req) => req.id === Number(value),
                );
                onRequisitionChange?.(requisition);
                field.onChange(Number(value));
              }}
              // TODO: Look into how to clean this up, along with FormNumberSelect and TableCellNumberSelect.
              value={field.value ? (field.value as number).toString() : ""}
            >
              <FormControl>
                {/* TODO: Clean up the need for this span wrapping logic */}
                {/* The selected RequisitionSelectOption will be wrapped in a span, so we must ensure that span is flexed and the width is full. h-fit overrides fixed h-10 */}
                <SelectTrigger className="h-fit [&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:justify-between">
                  <SelectValue placeholder="Select requisition" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="text-slate-700">
                {requisitions.length > 0 ? (
                  requisitions.map((requisition) => (
                    <SelectItem
                      key={requisition.id}
                      // TODO: Clean up the need for this span wrapping logic
                      className="h-fit [&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:justify-between"
                      value={requisition.id.toString()}
                    >
                      <RequisitionSelectOption requisition={requisition} />
                    </SelectItem>
                  ))
                ) : (
                  // TODO: Spice up this empty state a bit, maybe add a link to the Reqs tab.
                  <SelectItem value="no-requisitions" className="pl-2" disabled>
                    No live/backfill requisitions with 0 active placements found
                    on this job.
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
